import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import socketService from '../services/socketService';

// Custom hook for Socket.IO integration
export function useSocket() {
  const { isAuthenticated } = useAuth();
  const [connectionState, setConnectionState] = useState(socketService.getConnectionState());
  const eventListenersRef = useRef(new Map());

  // Update connection state
  const updateConnectionState = useCallback(() => {
    setConnectionState(socketService.getConnectionState());
  }, []);

  // Connect/disconnect based on auth status
  useEffect(() => {
    if (isAuthenticated && !socketService.isSocketConnected()) {
      socketService.connect()
        .then(() => {
          updateConnectionState();
        })
        .catch(() => {
          updateConnectionState();
        });
    } else if (!isAuthenticated && socketService.isSocketConnected()) {
      socketService.disconnect();
      updateConnectionState();
    }
  }, [isAuthenticated, updateConnectionState]);

  // Listen for connection changes
  useEffect(() => {
    const handleSocketError = () => updateConnectionState();
    const handleAuthLogout = () => updateConnectionState();
    
    window.addEventListener('socket:error', handleSocketError);
    window.addEventListener('auth:logout', handleAuthLogout);
    
    return () => {
      window.removeEventListener('socket:error', handleSocketError);
      window.removeEventListener('auth:logout', handleAuthLogout);
    };
  }, [updateConnectionState]);

  // Subscribe to socket events
  const on = useCallback((event, handler) => {
    const unsubscribe = socketService.on(event, handler);
    
    // Store unsubscribe function for cleanup
    if (!eventListenersRef.current.has(event)) {
      eventListenersRef.current.set(event, new Set());
    }
    eventListenersRef.current.get(event).add(unsubscribe);
    
    return unsubscribe;
  }, []);

  // Unsubscribe from events
  const off = useCallback((event, handler) => {
    socketService.off(event, handler);
    
    // Clean up stored unsubscribe function
    if (eventListenersRef.current.has(event)) {
      eventListenersRef.current.get(event).forEach(unsubscribe => unsubscribe());
      eventListenersRef.current.delete(event);
    }
  }, []);

  // Clean up all event listeners
  useEffect(() => {
    return () => {
      eventListenersRef.current.forEach((unsubscribes) => {
        unsubscribes.forEach(unsubscribe => unsubscribe());
      });
      eventListenersRef.current.clear();
    };
  }, []);

  return {
    ...connectionState,
    on,
    off,
    joinDashboard: socketService.joinDashboard.bind(socketService),
    leaveDashboard: socketService.leaveDashboard.bind(socketService),
    joinWidget: socketService.joinWidget.bind(socketService),
    leaveWidget: socketService.leaveWidget.bind(socketService),
    requestMetricsUpdate: socketService.requestMetricsUpdate.bind(socketService),
    requestActivitiesUpdate: socketService.requestActivitiesUpdate.bind(socketService),
  };
}

// Hook for dashboard real-time updates
export function useDashboardSocket() {
  const socket = useSocket();
  const [metrics, setMetrics] = useState(null);
  const [activities, setActivities] = useState([]);
  const [notifications, setNotifications] = useState([]);

  // Join dashboard room on mount
  useEffect(() => {
    if (socket.connected) {
      socket.joinDashboard();
      
      return () => {
        socket.leaveDashboard();
      };
    }
  }, [socket.connected]);

  // Listen for real-time updates
  useEffect(() => {
    if (!socket.connected) return;

    // Metrics updates
    const handleMetricsUpdate = (data) => {
      setMetrics(data.metrics);
    };

    // Activities updates
    const handleActivitiesUpdate = (data) => {
      setActivities(data.activities);
    };

    // New activity
    const handleActivityAdded = (data) => {
      setActivities(prev => [data.activity, ...prev.slice(0, 19)]); // Keep last 20
    };

    // System notifications
    const handleSystemNotification = (data) => {
      setNotifications(prev => [data, ...prev.slice(0, 4)]); // Keep last 5
      
      // Auto-remove notification after 5 seconds
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== data.id));
      }, 5000);
    };

    // Dashboard status
    const handleDashboardStatus = (data) => {
      if (data.metrics) setMetrics(data.metrics);
      if (data.recentActivities) setActivities(data.recentActivities);
    };

    // Widget updates
    const handleWidgetUpdate = (data) => {
      window.dispatchEvent(new CustomEvent('widget:updated', { detail: data }));
    };

    // Subscribe to events
    const unsubscribes = [
      socket.on('metrics-updated', handleMetricsUpdate),
      socket.on('activities-updated', handleActivitiesUpdate),
      socket.on('activity-added', handleActivityAdded),
      socket.on('system-notification', handleSystemNotification),
      socket.on('dashboard-status', handleDashboardStatus),
      socket.on('widget-updated', handleWidgetUpdate),
      socket.on('widget-created', handleWidgetUpdate),
      socket.on('widget-deleted', handleWidgetUpdate),
      socket.on('dashboard-refresh', () => {
        window.dispatchEvent(new CustomEvent('dashboard:refresh'));
      }),
    ];

    // Cleanup
    return () => {
      unsubscribes.forEach(unsubscribe => unsubscribe());
    };
  }, [socket.connected]);

  return {
    ...socket,
    metrics,
    activities,
    notifications,
    requestUpdate: () => {
      socket.requestMetricsUpdate();
      socket.requestActivitiesUpdate();
    },
  };
}

// Hook for widget real-time updates
export function useWidgetSocket(widgetId) {
  const socket = useSocket();
  const [interactions, setInteractions] = useState([]);
  const [widgetStats, setWidgetStats] = useState(null);

  // Join widget room
  useEffect(() => {
    if (socket.connected && widgetId) {
      socket.joinWidget(widgetId);
      
      return () => {
        socket.leaveWidget(widgetId);
      };
    }
  }, [socket.connected, widgetId]);

  // Listen for widget-specific updates
  useEffect(() => {
    if (!socket.connected || !widgetId) return;

    const handleInteractionCreated = (data) => {
      if (data.widgetId === widgetId) {
        setInteractions(prev => [data.interaction, ...prev.slice(0, 49)]); // Keep last 50
      }
    };

    const handleInteractionUpdated = (data) => {
      if (data.widgetId === widgetId) {
        setInteractions(prev => 
          prev.map(interaction => 
            interaction.id === data.interaction.id ? data.interaction : interaction
          )
        );
      }
    };

    const handleWidgetStatsUpdate = (data) => {
      if (data.widgetId === widgetId) {
        setWidgetStats(data.stats);
      }
    };

    // Subscribe to events
    const unsubscribes = [
      socket.on('interaction-created', handleInteractionCreated),
      socket.on('interaction-updated', handleInteractionUpdated),
      socket.on('widget-stats-updated', handleWidgetStatsUpdate),
    ];

    return () => {
      unsubscribes.forEach(unsubscribe => unsubscribe());
    };
  }, [socket.connected, widgetId]);

  return {
    ...socket,
    interactions,
    widgetStats,
    widgetId,
  };
}

// Hook for connection status monitoring
export function useSocketStatus() {
  const socket = useSocket();
  const [lastSeen, setLastSeen] = useState(Date.now());
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Monitor socket heartbeat
  useEffect(() => {
    if (!socket.connected) return;

    const handleHeartbeat = () => {
      setLastSeen(Date.now());
    };

    const unsubscribe = socket.on('heartbeat', handleHeartbeat);
    
    return unsubscribe;
  }, [socket.connected]);

  const connectionQuality = () => {
    if (!isOnline) return 'offline';
    if (!socket.connected) return 'disconnected';
    
    const timeSinceLastSeen = Date.now() - lastSeen;
    if (timeSinceLastSeen > 60000) return 'poor'; // 1 minute
    if (timeSinceLastSeen > 30000) return 'fair'; // 30 seconds
    return 'good';
  };

  return {
    isOnline,
    isConnected: socket.connected,
    connectionQuality: connectionQuality(),
    lastSeen,
    reconnectAttempts: socket.reconnectAttempts,
  };
}